import {
  AndroidIcon,
  AppleIcon,
  WebIcon,
  WindowsIcon,
} from "@/components/Icons";
import { useTranslation } from "react-i18next";

type ComparisonRow = {
  feature: string;
  free: string;
  premium: string;
};

// i18next hands back whatever the active locale holds, so a regressed or
// half-translated catalog could yield a string here. Onboarding is the very
// first screen after install — it must degrade to "no table" rather than throw
// and leave a new user staring at a blank overlay.
const isComparisonRow = (value: unknown): value is ComparisonRow =>
  !!value &&
  typeof value === "object" &&
  typeof (value as ComparisonRow).feature === "string" &&
  typeof (value as ComparisonRow).free === "string" &&
  typeof (value as ComparisonRow).premium === "string";

const PlanComparison = () => {
  const { t } = useTranslation();
  const rawRows = t("onboarding.planComparison.comparisonRows", {
    returnObjects: true,
  });
  const comparisonRows: ComparisonRow[] = Array.isArray(rawRows)
    ? rawRows.filter(isComparisonRow)
    : [];

  const platforms = [
    { key: "mobile", Icon: AndroidIcon },
    { key: "ios", Icon: AppleIcon },
    { key: "web", Icon: WebIcon },
    { key: "windows", Icon: WindowsIcon },
    { key: "mac", Icon: AppleIcon },
  ] as const;

  return (
    <div className="mt-4 flex w-full max-w-[520px] flex-col items-center text-center">
      {comparisonRows.length > 0 && (
        <div className="glass-tile w-full overflow-hidden rounded-2xl">
          <table
            className="w-full table-fixed border-collapse text-start"
            aria-label={t("onboarding.planComparison.comparisonLabel")}
          >
            <colgroup>
              <col className="w-[40%]" />
              <col className="w-[25%]" />
              <col className="w-[35%]" />
            </colgroup>
            <thead>
              <tr>
                <th className="px-2.5 py-3 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--text-tertiary)] min-[390px]:px-3 min-[390px]:text-[10.5px]">
                  {t("onboarding.planComparison.featureColumn")}
                </th>
                <th className="px-1.5 py-3 text-center text-[11px] font-black text-[var(--text-primary)] min-[390px]:px-2 min-[390px]:text-[12px]">
                  {t("onboarding.planComparison.freeTitle")}
                </th>
                <th className="bg-[var(--premium-primary)]/10 px-1.5 py-3 text-center text-[11px] font-black text-[var(--premium-primary)] min-[390px]:px-2 min-[390px]:text-[12px]">
                  {t("onboarding.planComparison.premiumTitle")}
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, rowIndex) => (
                <tr
                  key={`${rowIndex}-${row.feature}`}
                  className="border-t border-[var(--fill-2)]"
                >
                  <th
                    scope="row"
                    className="hyphens-auto px-2.5 py-2.5 text-[11px] font-bold leading-[1.3] text-[var(--text-primary)] min-[390px]:px-3 min-[390px]:text-[11.5px]"
                  >
                    {row.feature}
                  </th>
                  <td className="hyphens-auto px-1.5 py-2.5 text-center text-[10.5px] font-medium leading-[1.3] text-[var(--text-secondary)] min-[390px]:px-2 min-[390px]:text-[11px]">
                    {row.free}
                  </td>
                  <td className="hyphens-auto bg-[var(--premium-primary)]/7 px-1.5 py-2.5 text-center text-[10.5px] font-bold leading-[1.3] text-[var(--text-primary)] min-[390px]:px-2 min-[390px]:text-[11px]">
                    {row.premium}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="mt-2.5 w-full rounded-2xl border border-[var(--accent)]/20 bg-[var(--accent)]/8 px-3.5 py-3 text-[var(--text-primary)]">
        <p className="text-[13.5px] font-black text-[var(--text-primary)]">
          {t("onboarding.planComparison.crossPlatformTitle")}
        </p>
        {/* Wraps rather than truncates: at 8px with a 48px clamp every
            translated platform name ("Navegador", "Ordinateur") lost its tail
            on a 360px phone, which is most of this audience. */}
        <div className="mt-2.5 flex flex-wrap items-start justify-center gap-x-2.5 gap-y-2">
          {platforms.map(({ key, Icon }) => (
            <div key={key} className="flex w-12 flex-col items-center gap-1">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--accent)]/12 text-[var(--accent)]">
                <Icon size={14} />
              </span>
              <span className="text-center text-[9px] font-bold leading-tight text-[var(--text-secondary)]">
                {t(`onboarding.planComparison.platform_${key}`)}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PlanComparison;
